import log from 'loglevel';

export const generateRandomString = length => {
  const characters = 'abcdef0123456789';
  const charactersLength = characters.length;
  let result = '';

  // Create an array of 32-bit unsigned integers
  const randomValues = new Uint32Array(length);

  // Generate random values
  window.crypto.getRandomValues(randomValues);
  randomValues.forEach(value => {
    result += characters.charAt(value % charactersLength);
  });
  return result;
};

export const generateUUID = () => {
  return (
    generateRandomString(8) +
    '-' +
    generateRandomString(4) +
    '-' +
    generateRandomString(4) +
    '-' +
    generateRandomString(4) +
    '-' +
    generateRandomString(12)
  );
};

export const getDomainPart = (partcount = 2, host?) => {
  let base: string = window.location.hostname;
  if (host) {
    // handle full urls -> remove protocol, port, query parameter
    host = host.replaceAll(/^(\w+:\/\/)/g, '');
    host = host.replaceAll(/([/:?#].*)/g, '');
    base = host;
  }
  // localhost
  if (base === 'localhost') {
    return 'localhost';
  }
  // get domain part
  let retval = '';
  const parts = base.split('.');
  if (parts.length < partcount) {
    partcount = parts.length;
  }
  for (let n = parts.length - partcount; n < parts.length; n++) {
    retval += '.' + parts[n];
  }
  return retval;
};

export const stripSpecialQueryparams = (originuri: string, extraqueryparams: object): string => {
  const u = new URL(originuri);
  // query params reserved for our OIDC implementation
  for (const qp of [
    'code',
    'state',
    'acr_values',
    'session_state',
    'customstate',
    'iss',
    'acting-on-behalf-of',
    'identity',
    'error',
    'error_description',
    'motp',
    'kc_action',
    'kc_action_status',
  ]) {
    u.searchParams.delete(qp);
  }
  for (const qp in extraqueryparams) {
    u.searchParams.delete(qp);
  }
  return u.toString();
};

export function compareArrayAnyOrder(a: Array<any>, b: Array<any>) {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(value => b.includes(value));
}

/**
 * Somehow simplified recursive deep object comparison ignoring differences in order.
 * Use case is SetConfigEventPayload comparison, objects within arrays are not really supported
 * @returns true for equal objects, false otherwise
 */
export const compareObjectAnyOrder = (a, b) => {
  if ((a === undefined && b === undefined) || (a === null && b === null)) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (Object.keys(a).length != Object.keys(b).length) {
    return false;
  }
  return Object.keys(a).every(key => {
    // arrayA === arrayB is always false -> we have to check each element
    if (a[key] instanceof Array && b[key] instanceof Array) {
      return compareArrayAnyOrder(a[key], b[key]);
    } else if (['number', 'string', 'boolean', 'bigint', 'symbol', 'function', 'undefined'].includes(typeof a[key])) {
      return a[key] === b[key];
    } else {
      return compareObjectAnyOrder(a[key], b[key]);
    }
  });
};

export const isValidUri = (uri: string) => {
  try {
    return Boolean(new URL(uri));
  } catch (e) {
    log.error('oiam-oauth-wc: wrong url format given!', e);
    return false;
  }
};

export const decodeBase64url = (base64urlEncoded: string): string => {
  const base64NonUrlEncoded = base64urlEncoded.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/');
  const utf8BytesAsString = atob(base64NonUrlEncoded);
  const utf8ByteArray = Uint8Array.from(utf8BytesAsString, m => m.codePointAt(0) || 0);
  return new TextDecoder().decode(utf8ByteArray);
};

export const encodeBase64url = (s: string): string => {
  const encoder = new TextEncoder();
  const uint8array = encoder.encode(s);
  // Ganze Emojis z.B. sind außerhalb latin1 und bringen btoa zum platzen.
  // In einzelne Bytes zerhackt kommt btoa damit klar.
  const binaryString = String.fromCharCode(...uint8array);
  const base64 = btoa(binaryString);
  const base64url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  return base64url;
};

/**
 * Fast 32-bit hash (FNV-1a variant http://www.isthe.com/chongo/tech/comp/fnv/#FNV-1a )
 * No need for SHA-1 because it's only about change detection.
 * Sadly, Browser's crypto.digest() as an in-built function is `async` making it more cumbersome.
 */
export const fnv1a = (str: string): string => {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = (hash * 16777619) >>> 0; // Keep as 32-bit unsigned
  }
  return hash.toString(16);
};
